import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  ArrowLeft, Play, Pause, Pencil, FileText, FileSpreadsheet, Loader2, Inbox, RotateCcw, LogOut, Search, X, Phone, Clock, Volume2, VolumeX, AlertTriangle, Ship,
} from "lucide-react";
import api, { apiErrorMessage, normalizeEvent, API_ORIGIN } from "@/lib/api";
import { fetchCalls } from "@/lib/calls";
import { useAuth } from "@/context/AuthContext";
import { formatCoords, formatCourse, formatSpeed, formatDateTime, mpsToKnots, toDMS } from "@/utils/geo";
import ReplayMap from "@/components/ReplayMap";
import { interventionTime, fmtIntervention } from "@/lib/intervention";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { BRAND } from "@/branding";

function eventTitle(ev) {
  return ev.label || `${ev.nome} ${ev.cognome}`;
}

function InterventionCell({ ev }) {
  const info = interventionTime(ev);
  if (!info) return <span className="text-[#A3C2E0]/50">—</span>;
  if (!info.reached) {
    return (
      <span className="text-[#E8B923] whitespace-nowrap" title="La motovedetta non ha raggiunto il naufrago entro 0,1 NM">
        Non raggiunto{Number.isFinite(info.minNm) ? ` · min ${info.minNm.toFixed(1)} NM` : ""}
      </span>
    );
  }
  return (
    <div className="whitespace-nowrap">
      <div className="flex items-center gap-1.5 text-[#2E7D32]">
        <Ship className="h-3 w-3" />
        <span className="font-mono font-semibold">{fmtIntervention(info.seconds)}</span>
      </div>
      <div className="text-[#A3C2E0]">{ev.assigned_motovedetta_name}</div>
    </div>
  );
}

function buildCsv(ev) {
  const header = ["#", "timestamp", "coordinate_DMS", "velocita_nodi", "rotta_gradi", "rotta_dir"];
  const rows = (ev.track || []).map((p, i) => [
    i + 1,
    p.ts,
    `"${formatCoords(p.lat, p.lng)}"`,
    mpsToKnots(p.speed).toFixed(1),
    Math.round(p.course),
    formatCourse(p.course).split(" ")[1] || "",
  ]);
  return [header.join(","), ...rows.map((r) => r.join(","))].join("\n");
}

function exportCsv(ev) {
  const csv = buildCsv(ev);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `soccorso_${ev.number}_${ev.year}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function exportPdf(ev, eventCalls = []) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const marginX = 40;
  doc.setFillColor(10, 38, 71);
  doc.rect(0, 0, doc.internal.pageSize.getWidth(), 70, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(BRAND.reportTitle, marginX, 32);
  doc.setFontSize(10);
  doc.setTextColor(232, 185, 35);
  doc.text(BRAND.reportSubtitle, marginX, 50);

  doc.setTextColor(20, 20, 20);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  let y = 96;
  const totalCallSec = (eventCalls || []).reduce((a, c) => a + (c.duration_sec || 0), 0);
  const pad2 = (n) => String(n).padStart(2, "0");
  const durStr = (s) => `${pad2(Math.floor((s || 0) / 60))}:${pad2((s || 0) % 60)}`;
  const callSummaryStr = eventCalls && eventCalls.length
    ? `${eventCalls.length} (durata tot ${durStr(totalCallSec)})`
    : "Nessuna";
  const info = [
    [`Numero evento`, `#${ev.number}/${ev.year}`],
    [`Etichetta`, ev.label || "—"],
    [`Zona`, ev.zone_name],
    [`Operatore`, ev.operator_username],
    [`Segnalante`, `${ev.nome} ${ev.cognome}`],
    [`Telefono`, ev.telefono],
    [`Data arrivo`, formatDateTime(ev.arrival_ts)],
    [`Data chiusura`, formatDateTime(ev.ended_at)],
    [`Punti traccia`, String((ev.track || []).length)],
    [`Chiamate audio`, callSummaryStr],
  ];
  info.forEach(([k, v]) => {
    doc.setFont("helvetica", "bold");
    doc.text(`${k}:`, marginX, y);
    doc.setFont("helvetica", "normal");
    doc.text(String(v), marginX + 120, y);
    y += 18;
  });

  if (eventCalls && eventCalls.length) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(20, 20, 20);
    doc.text("Chiamate audio", marginX, y + 4);
    autoTable(doc, {
      startY: y + 10,
      head: [["#", "Inizio", "Fine", "Durata"]],
      body: eventCalls.map((c, i) => [i + 1, formatDateTime(c.started_at), formatDateTime(c.ended_at), durStr(c.duration_sec)]),
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [46, 125, 50], textColor: 255 },
      margin: { left: marginX, right: marginX },
    });
    y = doc.lastAutoTable.finalY + 12;
  }

  const body = (ev.track || []).map((p, i) => [
    i + 1,
    formatDateTime(p.ts),
    formatCoords(p.lat, p.lng),
    mpsToKnots(p.speed).toFixed(1),
    formatCourse(p.course),
  ]);
  autoTable(doc, {
    startY: y + 10,
    head: [["#", "Timestamp", "Coordinate (DMS)", "Velocità (nodi)", "Rotta"]],
    body,
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: [30, 90, 168], textColor: 255 },
    alternateRowStyles: { fillColor: [240, 244, 250] },
    margin: { left: marginX, right: marginX },
  });
  doc.save(`soccorso_${ev.number}_${ev.year}.pdf`);
}

export default function ArchivePage() {
  const { auth, logout } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [renameFor, setRenameFor] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [replayEvent, setReplayEvent] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const rafRef = useRef(null);
  const audioRef = useRef(null);
  const [volume, setVolume] = useState(100);
  const [muted, setMuted] = useState(false);
  const [calls, setCalls] = useState({});
  const [q, setQ] = useState("");
  const [zoneFilter, setZoneFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");

  const loadCalls = useCallback(() => {
    fetchCalls().then((list) => {
      const map = {};
      (list || []).forEach((c) => { (map[c.event_id] = map[c.event_id] || []).push(c); });
      setCalls(map);
    }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/events", { params: { scope: "archive" } })
      .then(({ data }) => { setEvents((data || []).map(normalizeEvent)); setError(null); })
      .catch((e) => setError(apiErrorMessage(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); loadCalls(); }, [load, loadCalls]);

  // replay animation
  useEffect(() => {
    if (!playing || !replayEvent) return;
    const evCalls = calls[replayEvent.id] || [];
    if (evCalls.some((c) => c.audio_path)) return; // con audio: l'orologio è l'audio
    let start = null;
    const durationMs = 8000;
    const from = progress;
    const step = (ts) => {
      if (start === null) start = ts;
      const t = (ts - start) / durationMs;
      const p = Math.min(1, from + t * (1 - from));
      setProgress(p);
      if (p < 1) rafRef.current = requestAnimationFrame(step);
      else setPlaying(false);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, replayEvent]);

  const openReplay = (ev) => {
    const evHasAudio = (calls[ev.id] || []).some((c) => c.audio_path);
    setReplayEvent(ev);
    setProgress(0);
    setPlaying(!evHasAudio); // con audio si parte in pausa (autoplay bloccato dal browser)
  };
  const closeReplay = () => {
    setPlaying(false);
    if (audioRef.current) { try { audioRef.current.pause(); audioRef.current.currentTime = 0; } catch (e) { console.debug("closeReplay audio", e); } }
    setReplayEvent(null);
    setProgress(0);
    cancelAnimationFrame(rafRef.current);
  };
  const replayAudioPath = replayEvent
    ? ((calls[replayEvent.id] || []).find((c) => c.audio_path) || {}).audio_path
    : null;
  const replayAudioUrl = replayAudioPath ? `${API_ORIGIN}/api/calls/audio/${replayAudioPath}` : null;

  const submitRename = async () => {
    if (!renameFor) return;
    setRenaming(true);
    try {
      await api.patch(`/events/${renameFor.id}`, { label: renameValue });
      toast.success("Evento rinominato");
      setRenameFor(null);
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setRenaming(false); }
  };

  const fmtDur = (s) => `${String(Math.floor((s || 0) / 60)).padStart(2, "0")}:${String((s || 0) % 60).padStart(2, "0")}`;
  const fmtTime = (iso) => {
    try { return new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }); }
    catch (e) { return "—"; }
  };
  const zoneOptions = Array.from(new Set(events.map((e) => e.zone_name).filter(Boolean))).sort();
  const filtered = events.filter((ev) => {
    if (zoneFilter && ev.zone_name !== zoneFilter) return false;
    if (dateFilter && (ev.arrival_ts || "").slice(0, 10) !== dateFilter) return false;
    if (q) {
      const hay = `${ev.nome || ""} ${ev.cognome || ""} ${ev.telefono || ""} ${ev.label || ""} ${ev.number || ""}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  }).sort((a, b) => (b.year - a.year) || (b.number - a.number));
  const clearFilters = () => { setQ(""); setZoneFilter(""); setDateFilter(""); };
  const hasFilters = q || zoneFilter || dateFilter;
  const allCalls = Object.values(calls).reduce((a, arr) => a.concat(arr), []);
  const totalCalls = allCalls.length;
  const avgCallSec = totalCalls ? Math.round(allCalls.reduce((a, c) => a + (c.duration_sec || 0), 0) / totalCalls) : 0;
  const interventions = events.map(interventionTime).filter((i) => i && i.reached && i.seconds != null);
  const avgInterventionSec = interventions.length
    ? Math.round(interventions.reduce((a, i) => a + i.seconds, 0) / interventions.length) : null;

  return (
    <div className="min-h-screen w-full flex flex-col">
      <header className="h-16 shrink-0 bg-[#0A2647] border-b border-[#1E5AA8] flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          {BRAND.logo && <img src={BRAND.logo} alt={BRAND.logoAlt} className="h-10 w-auto" />}
          <div>
            <div className="font-heading font-black text-white text-lg">Archivio Soccorsi</div>
            <div className="text-[10px] text-[#A3C2E0] uppercase tracking-widest">{auth?.username}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => { logout(); navigate("/login"); }} data-testid="archive-logout"
            className="border-[#D32F2F] bg-transparent text-[#ff8a8a] hover:bg-[#D32F2F] hover:text-white rounded-sm">
            <LogOut className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">Esci</span>
          </Button>
          <Button variant="outline" onClick={() => navigate("/")} data-testid="back-to-dashboard"
            className="border-[#1E5AA8] bg-transparent text-white hover:bg-[#1E5AA8] rounded-sm">
            <ArrowLeft className="h-4 w-4 mr-1" /> Dashboard
          </Button>
        </div>
      </header>

      <main className="flex-1 p-4 sm:p-6">
        <div data-testid="archive-retention-notice"
          className="mb-4 flex items-center gap-2 rounded-sm border border-[#E8B923]/40 bg-[#E8B923]/10 px-4 py-2.5 text-sm text-[#E8B923]">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          Archivio in fase di test. Le registrazioni verranno cancellate dopo 14 giorni.
        </div>
        {loading && (
          <div className="flex items-center justify-center py-24" data-testid="archive-loading">
            <Loader2 className="h-8 w-8 animate-spin text-[#E8B923]" />
          </div>
        )}
        {error && !loading && (
          <div className="max-w-md mx-auto bg-[#1E293B] border border-[#D32F2F] text-[#ff8a8a] rounded-sm p-4 flex items-center justify-between"
            data-testid="archive-error">
            <span>{error}</span>
            <Button size="sm" onClick={load} className="bg-[#D32F2F] rounded-sm"><RotateCcw className="h-4 w-4" /></Button>
          </div>
        )}
        {!loading && !error && events.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 text-[#A3C2E0]" data-testid="archive-empty">
            <Inbox className="h-12 w-12 mb-3" />
            <p>Nessun soccorso archiviato</p>
          </div>
        )}

        {!loading && !error && events.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4 max-w-4xl" data-testid="archive-summary">
            <div className="bg-[#113259] border border-[#1E5AA8] rounded-sm px-4 py-3 flex items-center gap-3">
              <Inbox className="h-6 w-6 text-[#E8B923] shrink-0" />
              <div>
                <div className="text-2xl font-heading font-black text-white leading-none" data-testid="summary-total">{events.length}</div>
                <div className="text-[10px] text-[#A3C2E0] uppercase tracking-wider mt-1">Soccorsi totali</div>
              </div>
            </div>
            <div className="bg-[#113259] border border-[#1E5AA8] rounded-sm px-4 py-3 flex items-center gap-3">
              <Phone className="h-6 w-6 text-[#2E7D32] shrink-0" />
              <div>
                <div className="text-2xl font-heading font-black text-white leading-none" data-testid="summary-calls">{totalCalls}</div>
                <div className="text-[10px] text-[#A3C2E0] uppercase tracking-wider mt-1">Chiamate audio</div>
              </div>
            </div>
            <div className="bg-[#113259] border border-[#1E5AA8] rounded-sm px-4 py-3 flex items-center gap-3">
              <Clock className="h-6 w-6 text-[#22d3ee] shrink-0" />
              <div>
                <div className="text-2xl font-heading font-black text-white leading-none font-mono" data-testid="summary-avg">{fmtDur(avgCallSec)}</div>
                <div className="text-[10px] text-[#A3C2E0] uppercase tracking-wider mt-1">Durata media chiamata</div>
              </div>
            </div>
            <div className="bg-[#113259] border border-[#1E5AA8] rounded-sm px-4 py-3 flex items-center gap-3">
              <Ship className="h-6 w-6 text-[#1E88E5] shrink-0" />
              <div>
                <div className="text-2xl font-heading font-black text-white leading-none font-mono" data-testid="summary-intervention">{fmtIntervention(avgInterventionSec)}</div>
                <div className="text-[10px] text-[#A3C2E0] uppercase tracking-wider mt-1">Tempo medio intervento</div>
              </div>
            </div>
          </div>
        )}

        {!loading && !error && events.length > 0 && (
          <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="archive-filters">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A3C2E0]" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="archive-search"
                placeholder="Cerca segnalante, telefono, etichetta o n°…"
                className="pl-8 bg-[#0A2647] border-[#1E5AA8] text-white placeholder:text-[#A3C2E0]/60" />
            </div>
            <div>
              <Label className="text-[10px] text-[#A3C2E0] uppercase tracking-wider block mb-1">Zona</Label>
              <select value={zoneFilter} onChange={(e) => setZoneFilter(e.target.value)} data-testid="archive-zone-filter"
                className="h-10 rounded-sm bg-[#0A2647] border border-[#1E5AA8] text-white text-sm px-2 min-w-[160px]">
                <option value="">Tutte le zone</option>
                {zoneOptions.map((z) => <option key={z} value={z}>{z}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[10px] text-[#A3C2E0] uppercase tracking-wider block mb-1">Data arrivo</Label>
              <Input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} data-testid="archive-date-filter"
                className="bg-[#0A2647] border-[#1E5AA8] text-white [color-scheme:dark] w-[160px]" />
            </div>
            {hasFilters && (
              <Button variant="outline" onClick={clearFilters} data-testid="archive-clear-filters"
                className="border-[#1E5AA8] bg-transparent text-white hover:bg-[#1E5AA8] rounded-sm h-10">
                <X className="h-4 w-4 mr-1" /> Azzera
              </Button>
            )}
            <div className="text-xs text-[#A3C2E0] ml-auto self-center" data-testid="archive-count">
              {filtered.length} di {events.length} soccorsi
            </div>
          </div>
        )}

        {!loading && !error && events.length > 0 && filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-[#A3C2E0]" data-testid="archive-no-results">
            <Search className="h-10 w-10 mb-3" />
            <p>Nessun risultato per i filtri selezionati</p>
            <Button size="sm" onClick={clearFilters} className="mt-3 bg-[#1E5AA8] rounded-sm">Azzera filtri</Button>
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <div className="bg-[#113259] border border-[#1E5AA8] rounded-sm overflow-x-auto" data-testid="archive-table">
            <Table>
              <TableHeader>
                <TableRow className="border-[#1E5AA8] hover:bg-transparent">
                  <TableHead className="text-[#E8B923]">Evento</TableHead>
                  <TableHead className="text-[#E8B923]">Zona</TableHead>
                  <TableHead className="text-[#E8B923]">Segnalante</TableHead>
                  <TableHead className="text-[#E8B923]">Data arrivo</TableHead>
                  <TableHead className="text-[#E8B923]">Chiamate audio</TableHead>
                  <TableHead className="text-[#E8B923]">Intervento</TableHead>
                  <TableHead className="text-[#E8B923] text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((ev) => (
                  <TableRow key={ev.id} className="border-[#1E5AA8]/50 hover:bg-[#0A2647]/50" data-testid={`archive-row-${ev.number}`}>
                    <TableCell>
                      <div className="font-mono text-[#E8B923]">#{ev.number}/{ev.year}</div>
                      {ev.label && <div className="text-xs text-white">{ev.label}</div>}
                    </TableCell>
                    <TableCell className="text-white text-sm">{ev.zone_name}</TableCell>
                    <TableCell className="text-white text-sm">
                      <div>{ev.nome} {ev.cognome}</div>
                      <div className="font-mono text-xs text-[#A3C2E0]">{ev.telefono}</div>
                    </TableCell>
                    <TableCell className="text-white text-xs font-mono">{formatDateTime(ev.arrival_ts)}</TableCell>
                    <TableCell className="text-xs" data-testid={`calls-${ev.number}`}>
                      {(calls[ev.id] && calls[ev.id].length) ? (
                        <div className="space-y-0.5">
                          {calls[ev.id].map((c, i) => (
                            <div key={`${ev.id}-${c.started_at}-${i}`} className="flex items-center gap-1.5 text-[#A3C2E0] whitespace-nowrap">
                              <Phone className="h-3 w-3 text-[#2E7D32]" />
                              <span className="font-mono">{fmtTime(c.started_at)}</span>
                              <span className="text-[#E8B923] font-mono">{fmtDur(c.duration_sec)}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-[#A3C2E0]/50">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs" data-testid={`intervention-${ev.number}`}>
                      <InterventionCell ev={ev} />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1 flex-wrap">
                        <Button size="sm" variant="outline" data-testid={`rename-${ev.number}`}
                          onClick={() => { setRenameFor(ev); setRenameValue(ev.label || ""); }}
                          className="border-[#1E5AA8] bg-transparent text-white hover:bg-[#1E5AA8] rounded-sm h-8">
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" data-testid={`replay-${ev.number}`}
                          onClick={() => openReplay(ev)}
                          className="border-[#E8B923] bg-transparent text-[#E8B923] hover:bg-[#E8B923] hover:text-[#0A2647] rounded-sm h-8">
                          <Play className="h-3.5 w-3.5 mr-1" /> Replay
                        </Button>
                        <Button size="sm" variant="outline" data-testid={`csv-${ev.number}`}
                          onClick={() => exportCsv(ev)}
                          className="border-[#1E5AA8] bg-transparent text-white hover:bg-[#1E5AA8] rounded-sm h-8">
                          <FileSpreadsheet className="h-3.5 w-3.5 mr-1" /> CSV
                        </Button>
                        <Button size="sm" variant="outline" data-testid={`pdf-${ev.number}`}
                          onClick={() => exportPdf(ev, calls[ev.id] || [])}
                          className="border-[#D32F2F] bg-transparent text-[#ff8a8a] hover:bg-[#D32F2F] hover:text-white rounded-sm h-8">
                          <FileText className="h-3.5 w-3.5 mr-1" /> PDF
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </main>

      {/* Rename dialog */}
      <Dialog open={!!renameFor} onOpenChange={(o) => !o && setRenameFor(null)}>
        <DialogContent className="bg-[#113259] border border-[#1E5AA8] text-white rounded-sm" data-testid="rename-dialog">
          <DialogHeader><DialogTitle className="font-heading">Rinomina evento</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs text-[#A3C2E0]">Etichetta</Label>
            <Input value={renameValue} onChange={(e) => setRenameValue(e.target.value)}
              placeholder="Es. Motovedetta CP 305 - dispersi" data-testid="rename-input"
              className="bg-[#0A2647] border-[#1E5AA8] text-white" />
          </div>
          <DialogFooter>
            <Button onClick={submitRename} disabled={renaming} data-testid="rename-submit"
              className="bg-[#E8B923] hover:bg-[#cfa415] text-[#0A2647] font-bold rounded-sm">
              {renaming ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Replay dialog */}
      <Dialog open={!!replayEvent} onOpenChange={(o) => !o && closeReplay()}>
        <DialogContent className="bg-[#113259] border border-[#1E5AA8] text-white rounded-sm max-w-3xl" data-testid="replay-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Replay percorso — {replayEvent && `#${replayEvent.number}/${replayEvent.year}`} {replayEvent && `· ${eventTitle(replayEvent)}`}
            </DialogTitle>
          </DialogHeader>
          {replayEvent && (
            <>
              <ReplayMap track={replayEvent.track} mvTrack={replayEvent.mv_track || []} progress={progress}
                mvName={replayEvent.assigned_motovedetta_name} />
              <div className="flex items-center gap-4 mt-2 text-xs text-[#A3C2E0]" data-testid="replay-legend">
                <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full bg-[#D32F2F] border border-white" /> Naufrago</span>
                {(replayEvent.mv_track || []).length > 0 ? (
                  <span className="flex items-center gap-1.5" data-testid="replay-legend-mv">
                    <span className="inline-block h-2.5 w-2.5 rounded-sm bg-[#1E88E5] border border-white" />
                    Motovedetta {replayEvent.assigned_motovedetta_name || ""}
                  </span>
                ) : (
                  <span className="italic opacity-70" data-testid="replay-legend-no-mv">Nessuna traccia motovedetta</span>
                )}
                {(() => {
                  const info = interventionTime(replayEvent);
                  if (!info) return null;
                  return (
                    <span className="ml-auto flex items-center gap-1.5" data-testid="replay-intervention">
                      <Clock className="h-3.5 w-3.5 text-[#22d3ee]" />
                      Tempo intervento:{" "}
                      {info.reached
                        ? <span className="font-mono font-semibold text-[#2E7D32]">{fmtIntervention(info.seconds)}</span>
                        : <span className="text-[#E8B923]">non raggiunto (min {info.minNm.toFixed(1)} NM)</span>}
                    </span>
                  );
                })()}
              </div>
              {replayAudioUrl && (
                <audio
                  ref={audioRef}
                  src={replayAudioUrl}
                  data-testid="replay-audio"
                  onLoadedMetadata={(e) => { e.currentTarget.volume = volume / 100; e.currentTarget.muted = muted; }}
                  onTimeUpdate={(e) => { const a = e.currentTarget; if (a.duration) setProgress(a.currentTime / a.duration); }}
                  onEnded={() => setPlaying(false)}
                  className="hidden"
                />
              )}
              {replayAudioUrl && (
                <div className="flex items-center gap-2 mt-2 text-xs text-[#2E7D32]" data-testid="replay-audio-badge">
                  <Volume2 className="h-4 w-4" /> Audio della chiamata disponibile
                </div>
              )}
              <div className="flex items-center gap-3 mt-2">
                <Button size="sm" data-testid="replay-playpause"
                  onClick={() => {
                    if (replayAudioUrl && audioRef.current) {
                      const a = audioRef.current;
                      if (playing) { a.pause(); setPlaying(false); }
                      else { a.play().catch(() => {}); setPlaying(true); }
                    } else {
                      setPlaying((p) => !p);
                    }
                  }}
                  className="bg-[#E8B923] hover:bg-[#cfa415] text-[#0A2647] rounded-sm">
                  {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                </Button>
                <Button size="sm" variant="outline" data-testid="replay-restart"
                  onClick={() => {
                    if (replayAudioUrl && audioRef.current) {
                      const a = audioRef.current;
                      a.currentTime = 0; a.play().catch(() => {});
                      setProgress(0); setPlaying(true);
                    } else {
                      setProgress(0); setPlaying(true);
                    }
                  }}
                  className="border-[#1E5AA8] bg-transparent text-white hover:bg-[#1E5AA8] rounded-sm">
                  <RotateCcw className="h-4 w-4" />
                </Button>
                <Slider value={[progress * 100]} max={100} step={1}
                  onValueChange={(v) => {
                    const p = v[0] / 100;
                    if (replayAudioUrl && audioRef.current) {
                      const a = audioRef.current;
                      if (a.duration) a.currentTime = p * a.duration;
                      setProgress(p);
                    } else {
                      setPlaying(false); setProgress(p);
                    }
                  }}
                  className="flex-1" data-testid="replay-slider" />
                <span className="font-mono text-xs text-[#A3C2E0] w-12 text-right">{Math.round(progress * 100)}%</span>
              </div>
              {replayAudioUrl && (
                <div className="flex items-center gap-2 mt-2" data-testid="replay-volume-row">
                  <button type="button" data-testid="replay-mute"
                    onClick={() => { const m = !muted; setMuted(m); if (audioRef.current) audioRef.current.muted = m; }}
                    className="text-[#A3C2E0] hover:text-white transition-colors" aria-label={muted ? "Riattiva audio" : "Muta"}>
                    {muted || volume === 0 ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                  </button>
                  <Slider value={[muted ? 0 : volume]} max={100} step={1} className="w-40" data-testid="replay-volume"
                    onValueChange={(v) => {
                      setVolume(v[0]); setMuted(false);
                      if (audioRef.current) { audioRef.current.volume = v[0] / 100; audioRef.current.muted = false; }
                    }} />
                  <span className="font-mono text-xs text-[#A3C2E0] w-10" data-testid="replay-volume-value">{muted ? 0 : volume}%</span>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
